import {
  PRICE_SHEET_COMPANY,
  customerCost,
  PRICE_SHEET_ORDER_SITE,
  formatPrice,
  formatWeight,
  seasonLabel,
  tierMinimum,
  type PriceSheetData,
  type PriceSheetFlavor,
} from './priceSheet'

/** A square JPEG ready to embed: raw bytes as a binary (0-255) string. */
export interface PdfPhoto {
  width: number
  height: number
  jpeg: string
}

// ─── Page geometry (US Letter, points) ──────────────────────────────────────
const PAGE_W = 612
const PAGE_H = 792
const MARGIN = 40
const RIGHT = PAGE_W - MARGIN // 572
const FOOTER_Y = 24
const BOTTOM_LIMIT = 48

const PHOTO = 62
const ROW_H = 76
const X_PHOTO = MARGIN
const X_TEXT = MARGIN + PHOTO + 12
const TEXT_W = 206
const X_SIZE = 330
const X_SKU = 400
const X_MSRP_RIGHT = 512
const X_COST_RIGHT = RIGHT

// Warm accent for headings (RGB 0-1).
const ACCENT = '0.70 0.27 0.09'

// ─── helpers ─────────────────────────────────────────────────────────────────

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

// Unicode punctuation that WinAnsiEncoding has under a different code.
const WIN_ANSI: Record<number, number> = {
  0x2013: 0x96,
  0x2014: 0x97,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2026: 0x85,
}

/** Escape a text string for a PDF literal and map it to WinAnsi. */
function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    let code = ch.codePointAt(0) ?? 0
    if (code > 255) code = WIN_ANSI[code] ?? 0x3f // '?'
    const c = String.fromCharCode(code)
    if (c === '\\') out += '\\\\'
    else if (c === '(') out += '\\('
    else if (c === ')') out += '\\)'
    else out += c
  }
  return out
}

/** Approximate Helvetica advance width, good enough to wrap and right-align. */
function textWidth(s: string, size: number, bold = false): number {
  let units = 0
  for (const ch of s) {
    if (ch === '—') units += 1000
    else if (/[0-9$]/.test(ch)) units += 556
    else if (/[.,:;'!|il ]/.test(ch)) units += 278
    else if (/[A-Z]/.test(ch)) units += 680
    else if (/[mwMW]/.test(ch)) units += 833
    else units += 520
  }
  return (units / 1000) * size * (bold ? 1.05 : 1)
}

function wrap(s: string, size: number, maxWidth: number, maxLines: number): string[] {
  const words = s.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const next = current ? `${current} ${word}` : word
    if (textWidth(next, size) <= maxWidth || !current) {
      current = next
      continue
    }
    lines.push(current)
    current = word
    if (lines.length === maxLines) break
  }
  if (lines.length < maxLines && current) lines.push(current)
  const used = lines.join(' ').split(/\s+/).filter(Boolean).length
  if (used < words.length && lines.length > 0) {
    let last = lines[lines.length - 1] ?? ''
    while (last && textWidth(`${last}…`, size) > maxWidth) last = last.replace(/\s*\S+$/, '')
    lines[lines.length - 1] = `${last}…`
  }
  return lines
}

function clip(s: string, size: number, maxWidth: number, bold = false): string {
  if (textWidth(s, size, bold) <= maxWidth) return s
  let out = s
  while (out.length > 1 && textWidth(`${out}…`, size, bold) > maxWidth) out = out.slice(0, -1)
  return `${out.trimEnd()}…`
}

// ─── content-stream operator builders ────────────────────────────────────────

type Font = 'F1' | 'F2'

function text(x: number, y: number, size: number, font: Font, value: string, rgb = '0 0 0'): string {
  return `BT ${rgb} rg /${font} ${num(size)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${pdfText(value)}) Tj ET\n`
}

function textRight(right: number, y: number, size: number, font: Font, value: string, rgb = '0 0 0'): string {
  return text(right - textWidth(value, size, font === 'F2'), y, size, font, value, rgb)
}

function line(x1: number, y1: number, x2: number, y2: number, width = 1, gray = 0): string {
  return `${num(width)} w ${num(gray)} ${num(gray)} ${num(gray)} RG ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`
}

function fillRect(x: number, y: number, w: number, h: number, rgb: string): string {
  return `${rgb} rg ${num(x)} ${num(y)} ${num(w)} ${num(h)} re f 0 0 0 rg\n`
}

function image(name: string, x: number, y: number, w: number, h: number): string {
  return `q ${num(w)} 0 0 ${num(h)} ${num(x)} ${num(y)} cm /${name} Do Q\n`
}

// ─── PDF assembly ────────────────────────────────────────────────────────────

function buildPdf(pageContents: string[], images: PdfPhoto[]): Blob {
  const objects: { num: number; body: string }[] = []
  const firstImage = 6
  const firstPage = firstImage + images.length
  const kids = pageContents.map((_, i) => `${firstPage + 2 * i} 0 R`)

  const xObjects = images.map((_, i) => `/Im${i} ${firstImage + i} 0 R`).join(' ')
  objects.push({ num: 1, body: '<< /Type /Catalog /Pages 2 0 R >>' })
  objects.push({ num: 2, body: `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageContents.length} >>` })
  objects.push({ num: 3, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>' })
  objects.push({ num: 4, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>' })
  objects.push({
    num: 5,
    body: `<< /Font << /F1 3 0 R /F2 4 0 R >>${xObjects ? ` /XObject << ${xObjects} >>` : ''} >>`,
  })

  images.forEach((img, i) => {
    objects.push({
      num: firstImage + i,
      body:
        `<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.jpeg.length} >>\n` +
        `stream\n${img.jpeg}\nendstream`,
    })
  })

  pageContents.forEach((content, i) => {
    const pageObj = firstPage + 2 * i
    objects.push({
      num: pageObj,
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources 5 0 R /Contents ${pageObj + 1} 0 R >>`,
    })
    objects.push({
      num: pageObj + 1,
      body: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    })
  })

  objects.sort((a, b) => a.num - b.num)

  // Built as a binary string (one char per byte) so JPEG bytes pass through.
  let pdf = '%PDF-1.4\n%\xe2\xe3\xcf\xd3\n'
  const offsets: number[] = []
  for (const obj of objects) {
    offsets[obj.num] = pdf.length
    pdf += `${obj.num} 0 obj\n${obj.body}\nendobj\n`
  }

  const xrefOffset = pdf.length
  const total = objects.length + 1
  pdf += `xref\n0 ${total}\r\n`
  pdf += '0000000000 65535 f\r\n'
  for (let n = 1; n < total; n++) {
    pdf += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n\r\n`
  }
  pdf += `trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  const bytes = new Uint8Array(pdf.length)
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff
  return new Blob([bytes], { type: 'application/pdf' })
}

// ─── photos ──────────────────────────────────────────────────────────────────

const PHOTO_PX = 200

function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    if (crossOrigin) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image failed to load'))
    img.src = src
  })
}

/** Center-crop to a small square JPEG so 50+ photos still fit in an email. */
async function toSquareJpeg(src: string, crossOrigin: boolean): Promise<PdfPhoto> {
  const img = await loadImage(src, crossOrigin)
  const side = Math.min(img.naturalWidth, img.naturalHeight)
  if (!side) throw new Error('Empty image')
  const canvas = document.createElement('canvas')
  canvas.width = PHOTO_PX
  canvas.height = PHOTO_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, PHOTO_PX, PHOTO_PX)
  ctx.drawImage(
    img,
    (img.naturalWidth - side) / 2,
    (img.naturalHeight - side) / 2,
    side,
    side,
    0,
    0,
    PHOTO_PX,
    PHOTO_PX
  )
  const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
  return { width: PHOTO_PX, height: PHOTO_PX, jpeg: atob(dataUrl.slice(dataUrl.indexOf(',') + 1)) }
}

// Photos already shrunk this session, by URL, so a second sheet is quick.
const photoCache = new Map<string, PdfPhoto>()

type StoredPhotoFetcher = (fileId: string) => Promise<{ type: string; data: string } | null>

/**
 * Loads every flavor's photo. Retool Storage photos come through the
 * `fetchers` (base64 from the backend, so the canvas isn't tainted), one
 * download at a time per fetcher; any other URL is tried directly. Photos
 * that fail are left out.
 */
export async function loadPriceSheetPhotos(
  flavors: PriceSheetFlavor[],
  fetchers: StoredPhotoFetcher[]
): Promise<Map<string, PdfPhoto>> {
  const photos = new Map<string, PdfPhoto>()
  const queue: PriceSheetFlavor[] = []
  for (const flavor of flavors) {
    if (!flavor.photo_url) continue
    const cached = photoCache.get(flavor.photo_url)
    if (cached) photos.set(flavor.flavor, cached)
    else queue.push(flavor)
  }

  const worker = async (fetchStoredPhoto: StoredPhotoFetcher) => {
    for (let next = queue.shift(); next; next = queue.shift()) {
      const flavor = next
      const url = flavor.photo_url
      if (!url) continue
      try {
        let src: string | null = null
        let crossOrigin = false
        if (flavor.photo_file_id) {
          const file = await fetchStoredPhoto(flavor.photo_file_id)
          if (file?.data) src = `data:${file.type || 'image/jpeg'};base64,${file.data}`
        } else {
          src = url
          crossOrigin = true
        }
        if (!src) continue
        const photo = await toSquareJpeg(src, crossOrigin)
        photoCache.set(url, photo)
        photos.set(flavor.flavor, photo)
      } catch {
        // No photo for this flavor; the sheet shows a blank tile instead.
      }
    }
  }
  await Promise.all(fetchers.map(worker))
  return photos
}

// ─── drawing ──────────────────────────────────────────────────────────────────

function drawFooter(ops: string[], page: number, pageCount: number): void {
  ops.push(line(MARGIN, FOOTER_Y + 12, RIGHT, FOOTER_Y + 12, 0.5, 0.8))
  ops.push(
    text(
      MARGIN,
      FOOTER_Y,
      8,
      'F1',
      `${PRICE_SHEET_COMPANY}  •  Order online at ${PRICE_SHEET_ORDER_SITE}  •  Prices are per bag and subject to change.`,
      '0.4 0.4 0.4'
    )
  )
  ops.push(textRight(RIGHT, FOOTER_Y, 8, 'F1', `Page ${page} of ${pageCount}`, '0.4 0.4 0.4'))
}

/** Tier band with its minimum and the column labels; returns the y below it. */
function drawSection(ops: string[], top: number, tier: string, minimum: number | null, continued: boolean): number {
  ops.push(fillRect(MARGIN, top - 24, RIGHT - MARGIN, 24, '0.97 0.93 0.88'))
  ops.push(text(MARGIN + 8, top - 16, 12, 'F2', continued ? `${tier} (continued)` : tier, ACCENT))
  if (minimum != null) {
    ops.push(
      textRight(RIGHT - 8, top - 16, 9, 'F1', `Minimum ${minimum} bags per flavor (Small and Large combined)`, '0.3 0.3 0.3')
    )
  }
  const labels = top - 38
  ops.push(text(X_SIZE, labels, 8, 'F2', 'SIZE', '0.45 0.45 0.45'))
  ops.push(text(X_SKU, labels, 8, 'F2', 'SKU', '0.45 0.45 0.45'))
  ops.push(textRight(X_MSRP_RIGHT, labels, 8, 'F2', 'MSRP', '0.45 0.45 0.45'))
  ops.push(textRight(X_COST_RIGHT, labels, 8, 'F2', 'YOUR COST', '0.45 0.45 0.45'))
  return labels - 6
}

function drawFlavor(
  ops: string[],
  top: number,
  flavor: PriceSheetFlavor,
  photoName: string | null,
  discount: number
): void {
  const photoY = top - 6 - PHOTO
  if (photoName) {
    ops.push(image(photoName, X_PHOTO, photoY, PHOTO, PHOTO))
  } else {
    ops.push(fillRect(X_PHOTO, photoY, PHOTO, PHOTO, '0.95 0.95 0.95'))
  }

  let y = top - 18
  ops.push(text(X_TEXT, y, 12, 'F2', clip(flavor.flavor, 12, TEXT_W, true)))

  const lines: { value: string; font: Font; rgb: string }[] = []
  if (flavor.categories.length > 0) {
    lines.push({ value: flavor.categories.join(', '), font: 'F2', rgb: '0.4 0.4 0.4' })
  }
  const season = seasonLabel(flavor)
  if (season) lines.push({ value: `Available ${season} each year`, font: 'F2', rgb: ACCENT })
  if (flavor.description) {
    for (const ln of wrap(flavor.description, 8.5, TEXT_W, 4 - lines.length)) {
      lines.push({ value: ln, font: 'F1', rgb: '0.25 0.25 0.25' })
    }
  }
  for (const ln of lines.slice(0, 4)) {
    y -= 11
    ops.push(text(X_TEXT, y, 8.5, ln.font, clip(ln.value, 8.5, TEXT_W, ln.font === 'F2'), ln.rgb))
  }

  let sy = top - 18
  for (const size of flavor.sizes.slice(0, 4)) {
    const weight = formatWeight(size.weight_oz)
    ops.push(text(X_SIZE, sy, 10, 'F2', size.size))
    if (weight) ops.push(text(X_SIZE + textWidth(size.size, 10, true) + 4, sy, 8.5, 'F1', weight, '0.4 0.4 0.4'))
    ops.push(text(X_SKU, sy, 9, 'F1', size.sku, '0.3 0.3 0.3'))
    ops.push(textRight(X_MSRP_RIGHT, sy, 10, 'F1', formatPrice(size.price), '0.3 0.3 0.3'))
    ops.push(textRight(X_COST_RIGHT, sy, 11, 'F2', formatPrice(customerCost(size.price, discount))))
    sy -= 15
  }
}

/**
 * Builds the wholesale price sheet PDF: a header with each tier's minimum,
 * then every active flavor by tier with its photo, category, seasonal dates
 * and the SKU, MSRP and the customer's cost (MSRP less `discount`) of each size.
 */
export function buildPriceSheetPdf(data: PriceSheetData, photos: Map<string, PdfPhoto>, discount: number): Blob {
  const images: PdfPhoto[] = []
  const imageNames = new Map<string, string>()
  for (const flavor of data.flavors) {
    const photo = photos.get(flavor.flavor)
    if (!photo) continue
    imageNames.set(flavor.flavor, `Im${images.length}`)
    images.push(photo)
  }

  const pages: string[][] = []
  let ops: string[] = []
  const newPage = () => {
    ops = []
    pages.push(ops)
  }
  newPage()

  // ── Header ──
  const date = new Date(data.generated_at)
  const dateStr = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  ops.push(text(MARGIN, PAGE_H - MARGIN - 14, 24, 'F2', PRICE_SHEET_COMPANY, ACCENT))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 36, 13, 'F2', 'Wholesale Price Sheet'))
  ops.push(textRight(RIGHT, PAGE_H - MARGIN - 14, 10, 'F1', dateStr, '0.35 0.35 0.35'))
  ops.push(textRight(RIGHT, PAGE_H - MARGIN - 28, 10, 'F1', `Order at ${PRICE_SHEET_ORDER_SITE}`, '0.35 0.35 0.35'))

  let top = PAGE_H - MARGIN - 50
  const minimums = data.tiers.filter(t => t.minimum != null)
  if (minimums.length > 0) {
    ops.push(line(MARGIN, top, RIGHT, top, 0.75, 0.8))
    ops.push(text(MARGIN, top - 15, 9, 'F2', 'Order minimums, in bags per flavor (Small and Large count together):'))
    const summary = minimums.map(t => `${t.tier}: ${t.minimum}`).join('     ')
    ops.push(text(MARGIN, top - 29, 10, 'F1', summary))
    top -= 40
  }
  top -= 8

  if (data.flavors.length === 0) {
    ops.push(text(MARGIN, top - 20, 12, 'F1', 'No active wholesale products.'))
  }

  let currentTier: string | null | undefined
  let rowIndex = 0
  for (const flavor of data.flavors) {
    const tier = flavor.tier ?? 'Other'
    const minimum = tierMinimum(data, flavor.tier)
    const startsSection = tier !== currentTier
    const needed = (startsSection ? 44 : 0) + ROW_H

    if (top - needed < BOTTOM_LIMIT) {
      newPage()
      top = PAGE_H - MARGIN
      if (!startsSection) {
        top = drawSection(ops, top, tier, minimum, true)
        rowIndex = 0
      }
    }
    if (startsSection) {
      if (top < PAGE_H - MARGIN) top -= 10
      top = drawSection(ops, top, tier, minimum, false)
      currentTier = tier
      rowIndex = 0
    }

    if (rowIndex % 2 === 1) ops.push(fillRect(MARGIN, top - ROW_H, RIGHT - MARGIN, ROW_H, '0.985 0.98 0.975'))
    drawFlavor(ops, top, flavor, imageNames.get(flavor.flavor) ?? null, discount)
    top -= ROW_H
    ops.push(line(MARGIN, top, RIGHT, top, 0.4, 0.85))
    rowIndex++
  }

  pages.forEach((pageOps, i) => drawFooter(pageOps, i + 1, pages.length))
  return buildPdf(
    pages.map(p => `0 0 0 rg\n0 0 0 RG\n${p.join('')}`),
    images
  )
}
